import { ChangeDetectionStrategy, Component, OnDestroy } from "@angular/core";
import { Router, ActivatedRoute } from "@angular/router";
import { Subscription } from "rxjs/Subscription";
import { Observable } from "rxjs/Observable";
import { UserHomeService } from "./userHome.service";
import { TasksService } from "./tasks/tasks.service";
import { USERHOMECONSTANTS } from "./userHome.constants";
import { CONFIGURATION } from "../../app.constants";
import * as swal                from "sweetalert2";
import {
    User, Organisation, Task, Pagination, Application, Message,
    AccountSummary, ApplicationStatus, ApplicationClaimStatus, EmailTemplate}
    from "../../models";
import { UserAuthenticationService, CurrentUserService } from "../../services/shared";

@Component({
    template: require("./userHome.component.html"),
    changeDetection: ChangeDetectionStrategy.Default, 
    styles: [require("./userHome.component.less").toString()],
    providers: [UserHomeService, TasksService]
})

export class UserHomeComponent implements OnDestroy {
    subscriber: Subscription;
    updateUserSubscriber: Subscription;
    hasRequestAppViewSubscriber: Subscription;
    updateTasksCountSubscriber: Subscription;
    activeTabId: number = 0;
    tabTitle: string;
    totalCount: string;
    totalTasks: string;
    activeMessages = { messages: [], totalMessages: 2 };
    errorMessage: string;
    errors: Error[];
    isProcessing: boolean;
    impersonatingUser: string;
    activeUserDetails = new User();
    activeTasksList = new Array<Task>();
    activeMessageList = new Array<Message>();
    activeEmailTemplateList = new Array<EmailTemplate>();
    activeAccountList = new Array<AccountSummary>();
    applicationAccessList = new Array<Application>();
    applicationClaimStatuses: Observable<ApplicationClaimStatus[]>;
    applicationStatuses: Observable<ApplicationStatus[]>;
    application: Application;
    emailTemplate: EmailTemplate;
    isOrganisationAdmin: boolean;
    hasRequestAppViewPermission: boolean;
    bannerMessage:string;
    IsbannerMessageActive:boolean;
    IsOptInMessageActive: boolean;
    IsUserActivated: boolean;
    IsUserLinked: boolean;
    SSOLink: string;

    // this determines if the user should see a notification alert that tells them they have outstanding tasks or not. 
    // The original idea, all organisation admin except the site admins (NETiDSiteAdmin/NETiDAdmin) should receive the notification the moment they login.
    isGettingTaskNotifications: boolean;

    // the following is to indicate whether user is a global admin or not (NetIdSiteAdmin and NetIdAdmin)
    hasMessageViewPermission: boolean = false;
    isDiocesan: boolean = false;

    constructor(protected currentUserService: CurrentUserService, protected userAuthService: UserAuthenticationService,
        protected userHomeService: UserHomeService, protected cecRouter: Router,
        protected cecActivatedRoute: ActivatedRoute, protected taskService: TasksService) {
        this.isProcessing = false;
        this.errorMessage = "";
        this.impersonatingUser = "";
        this.isOrganisationAdmin = true;

        this.IsUserActivated = currentUserService.isActivated();
        this.IsUserLinked = currentUserService.isLinked();
        this.SSOLink = CONFIGURATION.baseUrls.ssoUrl;

        this.GetBanner();
        this.subscriber = userHomeService.updateConfirmed$.subscribe(
            totalItems => {
                this.totalCount = " (" + totalItems + ")";
            });

        this.updateUserSubscriber = userHomeService.updateUserDetailsConfirmed$.subscribe(
            user => { 
                this.activeUserDetails = user; 
            }
        );

        this.updateTasksCountSubscriber = userHomeService.updateTasksConfirmed$.subscribe(
            totalTasks =>  {
                this.totalTasks = totalTasks > 0 ? " (" + totalTasks + ")" : "";
            }
        );
        }

    // region: Event handlers
    ngOnInit() {
        try {
            if (!this.userAuthService.isAuthenticated) {
                this.cecRouter.navigate(["/login"]);
                return;
            } else if (this.currentUserService.token.needsSecretChanged === "True") {
                this.cecRouter.navigate(["/secret"]);
                return;
            } else if (this.currentUserService.token.missingMobileOrDob === "True") {
                this.cecRouter.navigate(["/forceContactInfoUpdate"]);
                return;
            }   
        } catch (e) {
            this.cecRouter.navigate(["/error"]);
            return;
        }
        this.tabTitle = USERHOMECONSTANTS.tabs.applications;
        let tabId = +this.cecActivatedRoute.snapshot.queryParams[USERHOMECONSTANTS.params.tabId];
        let userId = this.cecActivatedRoute.snapshot.queryParams[USERHOMECONSTANTS.params.userId];
        if (tabId) {
            this.activeTabId = tabId;
        }
        if (userId) {
            this.impersonatingUser = userId;
        } else {
            this.impersonatingUser = "";
        }

        this.determineIfUserHasAdminRights();
        this.determineIfUserShouldSeeNotifications();

        this.hasMessageViewPermission = this.currentUserService.IsGlobalAdmin();
        this.isDiocesan = this.currentUserService.IsDiocesanAdmin() || this.currentUserService.IsDiocesanUser();
        this.CanRequestAccess();
    }

    public GetBanner(): void {

        this.userHomeService
        .GetBanner()
        .map(response => response.json())
        .subscribe(
            response => {
         let banner = response.result;
         this.bannerMessage = banner.message;
         this.IsbannerMessageActive = banner.enabled;

        },
        (error) => {
            let parsedErrors = JSON.parse(error._body);
            if (parsedErrors) {
                this.errorMessage = parsedErrors.message;
            }
        });

    }

    // This method returns true if user has admin rights but not a site admin
    determineIfUserShouldSeeNotifications() {
        // we only check if the user isn't a global admin, because non-admin users will not be able to see any tasks anyway therefore their tasks count is 0 and won't see the notification pop up
        // however, the current user may have multiple orgs and isn't an admin in their main one, so this.currentUserService.isOrgAdmin() will sometimes return false
        this.isGettingTaskNotifications = !this.currentUserService.IsGlobalAdmin();
        if (this.isGettingTaskNotifications) {
            this.GetTasksList();
        }
    }

    // This method returns true if the current user has admin rights in at least one of their orgs
    // global admins are included
    determineIfUserHasAdminRights() {
        this.isOrganisationAdmin = 
        this.currentUserService.IsOrgAdmin() ||
        (!this.currentUserService.IsNetidUser() && !this.currentUserService.IsDiocesanUser());
    }

    // this method uses the same technique in the tasks.components.ts to fetch the tasks list and tasks total count
    // we are only interested in the total count at this stage
    public GetTasksList(): void {
        this.CanRequestAccess();
        let pagination = new Pagination();
        pagination = {
            totalItems: 0,
            currentPage: USERHOMECONSTANTS.pagination.currentPage,
            itemsPerPage: USERHOMECONSTANTS.pagination.itemsPerPage,
            maxSize: USERHOMECONSTANTS.pagination.maxSize
        };
        this.taskService
            .GetTasksList(pagination, USERHOMECONSTANTS.sorting.ascOrderString, "DateRequested")
            .map(response => response.json())
            .subscribe(
                response => {
                this.userHomeService.confirmTotalTasksUpdate(response.count); // get from the response

                // test if the parameter accountId present in the query string to see if the user is currently looking at someone else's account
                let userId = this.cecActivatedRoute.snapshot.queryParams[USERHOMECONSTANTS.params.userId];

                // only pop up the task notification if the user is looking at their own profile
                if (parseInt(response.count) > 0 && !userId) {
                    swal({   //   START BY FINDING A PLACE TO PUT THIS SWAL STATEMENT TO THE SUBSCRIBER OF TASKS SERVICE
                        title: "Welcome",
                        text: "You have outstanding tasks that require your attention. Please use the tasks pane to review your requests.",
                        showCancelButton: false,
                        showConfirmButton: false,
                        timer: 1500
                    });
                }
            },
            (error) => {
                let parsedErrors = JSON.parse(error._body);
                if (parsedErrors) {
                    this.errorMessage = parsedErrors.message;
                }
            });
    }

    private CanRequestAccess() : void{
        if(this.userAuthService.isAuthenticated) 
        {
         this.hasRequestAppViewSubscriber = this.userHomeService.GetOrganisations().subscribe(
            (response: Organisation[]) => {

                //extOrganisationId
                 let selectedOrgan = response.find( o => o.isMain);
                 this.hasRequestAppViewPermission = (selectedOrgan.extOrganisationId.toUpperCase()  !== '0778F56A-70FB-4A32-94D9-151828E21287');
                 if(!this.hasRequestAppViewPermission)
                 {
                     swal({   //   START BY FINDING A PLACE TO PUT THIS SWAL STATEMENT TO THE SUBSCRIBER OF TASKS SERVICE
                         title: "No Active Organisation",
                         text: "Add an organisation to your account via 'My Details' tab in order to 'access' applications!",
                         type: "warning",
                         showCancelButton: false,
                         showConfirmButton: false,
                         timer: 1500
                     });
                 }
            });
        }
    }
    onReloadMyhome() {
        this.determineIfUserHasAdminRights();
        this.CanRequestAccess();
    }

    onCancelImpersonation() {
        this.cecRouter.navigate(["/home"]);
    }

    onRefreshTaskList(tasks) {
        this.activeTasksList = tasks;
    }

    onRefreshMessageList(messages) {
        this.activeMessageList = messages;
    }

    // This method is the one called by the refreshList event 
    onRefreshEmailTemplateList(templates) {
        this.activeEmailTemplateList = templates;
    }

    onRefreshUserDetails(activeUser) {
        this.activeUserDetails = activeUser;
    }
    onRefreshApplication(application) {
        this.application = application;
    }
    onRefreshEmailTemplateForm(emailTemplate) {
        this.emailTemplate = emailTemplate;
    }

    onRefreshAccountList(accountSummary) {
        this.activeAccountList = accountSummary;
    }

    public GetApplicationStatus(): void {
        this.applicationStatuses = this.userHomeService.GetApplicationStatuses();
    }

    public GetApplicationClaimStatuses(): void {
        this.applicationClaimStatuses = this.userHomeService.GetApplicationClaimStatuses();
    }

    public onRefreshApplicationAccessList(applications) {
        this.GetApplicationClaimStatuses();
        this.GetApplicationStatus();
        this.applicationAccessList = applications;
    }

    ngOnDestroy() {
        this.subscriber.unsubscribe();
        this.updateUserSubscriber.unsubscribe();
        this.updateTasksCountSubscriber.unsubscribe();
        if(this.hasRequestAppViewSubscriber) {
        this.hasRequestAppViewSubscriber.unsubscribe();
        }
    }

    public setCurrentTab(tabIndex: number): void {
        if (tabIndex === 5) {
            this.GetApplicationClaimStatuses();
            this.GetApplicationStatus();
        }
        if(tabIndex !== 7){
            this.GetBanner();
           }
        this.activeTabId = tabIndex;
       
    };

    public ssoAccountLinkNavigation(): void {
        window.location.href = CONFIGURATION.baseUrls.ssoUrl;
    }
}




// WEBPACK FOOTER //
// ./src/app/modules/userHome/userHome.component.ts